import { deleteObject, ref } from "firebase/storage";
import { auth, firestore, storage } from "./init";
import {
  collection,
  doc,
  where,
  query,
  getDocs,
  Timestamp,
  deleteDoc,
  addDoc,
  setDoc,
  orderBy,
  limit,
  QueryDocumentSnapshot,
  updateDoc,
} from "firebase/firestore";
import { Bookmark, Query, QueryResult } from "../types/app";

export const getUID = (): Promise<string> => {
  return new Promise((resolve, reject) => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      unsubscribe();
      if (user) {
        const uid = user.uid;
        resolve(uid);
      }
    });
  });
};

export const getDocumentsByUUID = async (
  collectionName: string,
  uid?: string | undefined
) => {
  try {
    if (!uid) {
      uid = await getUID();
    }

    const querySnapshot = await getDocs(
      query(
        collection(firestore, collectionName),
        where("uid", "==", uid),
        where("deleted", "==", false)
      )
    );

    const documents: any[] = [];
    querySnapshot.forEach((doc) => {
      documents.push({ fileUid: doc.id, ...(doc.data() as any) });
    });

    return documents;
  } catch (error) {
    console.error("Error getting documents: ", error);
    throw error;
  }
};

export const deleteFileByName = async (
  fileName: string,
  uid?: string | undefined
) => {
  try {
    if (!uid) {
      uid = await getUID();
    }

    const q = query(
      collection(firestore, "files"),
      where("fileName", "==", fileName),
      where("uid", "==", uid),
      where("deleted", "==", false)
    );

    // Execute the query
    const querySnapshot = await getDocs(q);
    const docId = querySnapshot.docs[0].id;
    const docRef = ref(storage, `${uid}/${fileName}`);

    await updateDoc(doc(firestore, "files", docId), { deleted: true });
    // await deleteObject(docRef);
  } catch (error) {
    console.error("Error deleting file: ", error);
    throw error;
  }
};

export const addFeedback = async (
  answer: boolean,
  rating: number,
  relevantResults: number[],
  remarks: string,
  intendedResult: string,
  query: string,
  queryResults: QueryResult[],
  uid?: string | undefined
) => {
  try {
    if (!uid) {
      uid = await getUID();
    }

    addDoc(collection(firestore, "feedback"), {
      uid,
      intendedAnswer: answer,
      rating: rating,
      relevantResults: relevantResults,
      remarks: remarks,
      intendedResult: intendedResult,
      query: query,
      queryResults: queryResults,
    });
  } catch (error) {
    console.error("Error adding feedback: ", error);
    throw error;
  }
};

export const getNumFilesAndTotalFileSize = async (uid?: string | undefined) => {
  if (!uid) {
    uid = await getUID();
  }

  const q = query(
    collection(firestore, "files"),
    where("uid", "==", uid),
    where("deleted", "==", false)
  );
  let size = 0;

  const querySnapshot = await getDocs(q);
  querySnapshot.docs.forEach((doc) => {
    const docSize = parseFloat(
      doc.data().fileSize.substring(0, doc.data().fileSize.length - 3)
    );
    size += docSize;
  });
  return { size: size, numFiles: querySnapshot.docs.length };
};

export const addBookmarkToDB = async (
  bookmark: Bookmark,
  uid?: string | undefined
) => {
  if (!uid) {
    uid = await getUID();
  }

  addDoc(collection(firestore, "bookmark"), {
    uid,
    index: bookmark.index,
    pdfName: bookmark.pdfName,
    page: bookmark.page,
    title: bookmark.title,
    group: bookmark.group,
    answer: bookmark.answer || null,
    description: bookmark.description,
    displayText: bookmark.displayText,
    searchText: bookmark.searchText,
    deleted: false,
  });
};

export const deleteBookmarkFromDB = async (
  bookmark: Bookmark,
  uid?: string | undefined
) => {
  if (!uid) {
    uid = await getUID();
  }
  const collectionRef = collection(firestore, "bookmark");

  const querySnapshot = await getDocs(
    query(
      collectionRef,
      where("uid", "==", uid),
      where("deleted", "==", false),
      orderBy("index")
    )
  );

  const bookmarks: Bookmark[] = [];
  querySnapshot.forEach((doc) => {
    bookmarks.push(doc.data() as Bookmark);
  });

  const bookmarkIndex = bookmarks.findIndex((b) => b.index === bookmark.index);

  await updateDoc(querySnapshot.docs[bookmarkIndex].ref, {
    deleted: true,
    index: -1,
  });
  const updatedQuery = querySnapshot.docs
    .slice(0, bookmarkIndex)
    .concat(querySnapshot.docs.slice(bookmarkIndex + 1));

  const updatedBookmarks = bookmarks
    .filter((b) => b.index !== bookmark.index)
    .map((b, i) => ({
      ...b,
      index: i + 1,
    }));

  await Promise.all(
    updatedBookmarks.map((b, idx) => setDoc(updatedQuery[idx].ref, b))
  );
};

export const addQuery = async (
  query: string,
  clusters: string[],
  queryResults: QueryResult[],
  uid?: string | undefined
) => {
  try {
    if (!uid) {
      uid = await getUID();
    }

    const pastQueries = await getPastQueries();
    const latestQueryResult = pastQueries && pastQueries[0];
    const latestIdx = (latestQueryResult && latestQueryResult.idx + 1) || 0;

    addDoc(collection(firestore, "queryHistory"), {
      uid,
      idx: latestIdx,
      query: query,
      clusters: clusters,
      queryResults: queryResults,
      timestamp: Timestamp.fromDate(new Date()),
      deleted: false,
    });
  } catch (error) {
    console.error("Error adding QueryResult: ", error);
    throw error;
  }
};

export const getPastQueries = async (uid?: string | undefined) => {
  if (!uid) {
    uid = await getUID();
  }
  const collectionRef = collection(firestore, "queryHistory");

  const querySnapshot = await getDocs(
    query(
      collectionRef,
      where("uid", "==", uid),
      where("deleted", "==", false),
      orderBy("idx", "desc")
    )
  );

  return querySnapshot.docs.length === 0
    ? undefined
    : querySnapshot.docs.map((doc: QueryDocumentSnapshot) => doc.data());
};

export const deleteQueryFromDB = async (
  queryToDelete: Query,
  uid?: string | undefined
) => {
  if (!uid) {
    uid = await getUID();
  }
  const collectionRef = collection(firestore, "queryHistory");

  const querySnapshot = await getDocs(
    query(
      collectionRef,
      where("uid", "==", uid),
      where("deleted", "==", false),
      orderBy("idx", "asc")
    )
  );

  const queries: Query[] = [];
  querySnapshot.forEach((doc) => {
    queries.push(doc.data() as Query);
  });

  const queryIndex = queries.findIndex((b) => b.idx === queryToDelete.idx);

  await updateDoc(querySnapshot.docs[queryIndex].ref, {
    deleted: true,
    idx: -1,
  });
  const updatedQuery = querySnapshot.docs
    .slice(0, queryIndex)
    .concat(querySnapshot.docs.slice(queryIndex + 1));

  const updatedQueries = queries
    .filter((b) => b.idx !== queryToDelete.idx)
    .map((b, i) => ({
      ...b,
      idx: i,
    }));

  await Promise.all(
    updatedQueries.map((b, idx) => setDoc(updatedQuery[idx].ref, b))
  );
};

export const updateMindmapState = async (
  state: string,
  uid?: string | undefined
) => {
  if (!uid) {
    uid = await getUID();
  }
  const collectionRef = collection(firestore, "mindMap");

  const querySnapshot = await getDocs(
    query(collectionRef, where("uid", "==", uid))
  );

  if (querySnapshot.docs.length !== 0) {
    await deleteDoc(querySnapshot.docs[0].ref);
  }
  await addDoc(collectionRef, {
    uid,
    state: state,
  });
};

export const getMindmapState = async (uid?: string | undefined) => {
  if (!uid) {
    uid = await getUID();
  }
  const collectionRef = collection(firestore, "mindMap");

  const querySnapshot = await getDocs(
    query(collectionRef, where("uid", "==", uid))
  );

  return querySnapshot.docs.length !== 0 ? querySnapshot.docs[0].data() : null;
};

export const addUserFeedback = async (
  feedback: string,
  uid?: string | undefined
) => {
  try {
    if (!uid) {
      uid = await getUID();
    }

    addDoc(collection(firestore, "userFeedback"), {
      uid,
      feedback: feedback,
      timestamp: Timestamp.fromDate(new Date()),
    });
  } catch (error) {
    console.error("Error adding Feedback: ", error);
    throw error;
  }
};
